/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import { cloneDeep, isEmpty } from 'lodash';
import Types from '../constants/actionTypes';

const initialState = {
  initialDataLoading: true,
  isFetching: false,
  isSending: false,
  target: { id: 0 },
  targets: [],
  burnupData: null
};

const updateTarget = (targets = [], newTarget = {}) =>
  targets.map((target) => {
    if (target.id === newTarget.id) {
      return newTarget;
    }
    return target;
  });

const removeWorkPackageFromTarget = (
  targets = [],
  targetId = 0,
  workPackage = {}
) => {
  const target = targets.find((t) => t.id === targetId);
  const newTarget = cloneDeep(target);
  newTarget.work_packages = newTarget.work_packages.filter(
    (wp) => wp.id !== workPackage.id
  );
  return targets.map((t) => {
    if (t.id === newTarget.id) {
      return newTarget;
    }
    return t;
  });
};

const removeWorkPackagesFromTarget = (
  targets = [],
  targetId = 0,
  workPackages = []
) => {
  const resultIds = workPackages.map((wp) => wp.id);
  const target = targets.find((t) => t.id === targetId);
  const newTarget = cloneDeep(target);
  newTarget.work_packages = newTarget.work_packages.filter(
    (wp) => !resultIds.includes(wp.id)
  );
  return targets.map((t) => {
    if (t.id === newTarget.id) {
      return newTarget;
    }
    return t;
  });
};

const addWorkPackageToTarget = (
  targets = [],
  targetId = 0,
  workPackage = {},
  children = []
) => {
  const target = targets.find((t) => t.id === targetId);
  const newTarget = cloneDeep(target);
  newTarget.work_packages.push(workPackage);
  if (!isEmpty(children)) {
    children.forEach((child) => newTarget.work_packages.push(child));
  }
  return targets.map((t) => {
    if (t.id === newTarget.id) {
      return newTarget;
    }
    return t;
  });
};

const addWorkPackagesToTarget = (
  targets = [],
  targetId = 0,
  workPackages = []
) => {
  const target = targets.find((t) => t.id === targetId);
  const newTarget = cloneDeep(target);
  newTarget.work_packages = newTarget.work_packages.concat(workPackages);
  return targets.map((t) => {
    if (t.id === newTarget.id) {
      return newTarget;
    }
    return t;
  });
};

const addWorkPackagesToAllTargets = (workPackages, state) => {
  const newTargets = [];
  state.targets.forEach((target) => {
    const updatedTarget = { ...target };
    updatedTarget.work_packages = workPackages.filter(
      (wp) => wp.target_id === updatedTarget.id
    );
    newTargets.push(updatedTarget);
  });
  return newTargets;
};

const targetReducer = (state = initialState, action) => {
  const { targets } = state;
  let updatedTargets;
  switch (action.type) {
    case Types.REQUEST_TARGET:
      return { ...state, isSending: true, isFetching: true };
    case Types.REQUEST_TARGET_FAILED:
      return { ...state, isSending: false, isFetching: false };
    case Types.RECEIVE_TARGETS:
      return {
        ...state,
        isFetching: false,
        targets: action.targets,
        isSending: false,
        initialDataLoading: false
      };
    case Types.ADD_WORK_PACKAGES_TO_TARGETS:
      return {
        ...state,
        isFetching: false,
        targets: addWorkPackagesToAllTargets(action.workPackages, state),
        initialDataLoading: false
      };
    case Types.GET_TARGET_BURNUP_DATA:
      return {
        ...state,
        isFetching: false,
        isSending: false,
        burnupData: action.burnupData
      };
    case Types.SET_TARGET:
      return {
        ...state,
        isFetching: false,
        notarget: false,
        target: action.target
      };
    case Types.SEND_TARGET:
      return { ...state, isSending: true };
    case Types.UPDATE_TARGET:
      return {
        ...state,
        targets: updateTarget(targets, action.target),
        isSending: false
      };
    case Types.ADD_TARGET:
      return {
        ...state,
        initialDataLoading: false,
        isFetching: false,
        isSending: false,
        targets: [...state.targets, action.target]
      };
    case Types.DELETE_TARGET:
      updatedTargets = state.targets.filter((p) => p.id !== action.targetId);

      return { ...state, targets: updatedTargets };
    case Types.ADD_WORK_PACKAGE_TO_TARGET:
      return {
        ...state,
        targets: addWorkPackageToTarget(
          targets,
          action.targetId,
          action.workPackage,
          action.children
        )
      };
    case Types.ADD_WORK_PACKAGES_TO_TARGET:
      return {
        ...state,
        targets: addWorkPackagesToTarget(
          targets,
          action.targetId,
          action.results
        )
      };

    case Types.REMOVE_WORK_PACKAGES_FROM_TARGET:
      return {
        ...state,
        targets: removeWorkPackagesFromTarget(
          targets,
          action.targetId,
          action.results
        )
      };
    case Types.REMOVE_WORK_PACKAGE_FROM_TARGET:
      return {
        ...state,
        targets: removeWorkPackageFromTarget(
          targets,
          action.targetId,
          action.workPackage
        )
      };
    case Types.CLEAR_ACTIVE_SPINNER:
      return {
        ...state,
        isFetching: false,
        isSending: false,
        initialDataLoading: false
      };
    default:
      return state;
  }
};

export default targetReducer;
